import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Wallet as WalletIcon, Download, Settings, History } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency } from "../../lib/utils-format";
import { PhoneInput } from "../../components/PhoneInput";
import { VerifyCodeDialog } from "../../components/VerifyCodeDialog";

export default function WalletPage() {
    const { t } = useTranslation();
    const { profile } = useAuth();
    const [wallet, setWallet] = useState(null);
    const [payoutMethod, setPayoutMethod] = useState(null);
    const [history, setHistory] = useState([]);
    const [showSetup, setShowSetup] = useState(false);
    const [setupData, setSetupData] = useState({});

    // Verification dialogs
    const [methodVerifyOpen, setMethodVerifyOpen] = useState(false);
    const [withdrawVerifyOpen, setWithdrawVerifyOpen] = useState(false);

    const countryCode = profile?.organizer_country?.code;
    const currency = profile?.organizer_country?.currency_code || "USD";

    const load = async () => {
        try {
            const [w, pm, h] = await Promise.all([
                api.get("/api/wallet"),
                api.get("/api/payout-method"),
                api.get("/api/payouts"),
            ]);
            setWallet(w.data);
            setPayoutMethod(pm.data);
            setHistory(h.data || []);
            if (pm.data) setSetupData(pm.data.details || {});
            else setSetupData(countryCode === "US" ? { payee_name: "", account_number: "", routing_number: "" } : { full_name: "", phone_code: "+509", phone: "" });
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (profile) load();
        // eslint-disable-next-line
    }, [profile]);

    const initiatePayoutMethod = async () => {
        const required = countryCode === "US" ? ["payee_name", "account_number", "routing_number"] : ["full_name", "phone"];
        for (const r of required) {
            if (!setupData[r]) {
                toast.error(t("auth.required"));
                return;
            }
        }
        try {
            await api.put("/api/payout-method", { details: setupData });
            toast.success(t("auth.code_sent"));
            setMethodVerifyOpen(true);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error(t("auth.too_many_requests"));
            else toast.error(t("common.error"));
        }
    };
    const confirmPayoutMethod = async (code) => {
        await api.post("/api/payout-method/confirm", { code });
    };

    const initiateWithdraw = async () => {
        try {
            await api.post("/api/payouts/request");
            toast.success(t("auth.code_sent"));
            setWithdrawVerifyOpen(true);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) toast.error(t("auth.too_many_requests"));
            else toast.error(e?.response?.data?.detail || t("common.error"));
        }
    };
    const confirmWithdraw = async (code) => {
        await api.post("/api/payouts/request/confirm", { code });
    };

    if (!wallet) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

    return (
        <div data-testid="wallet-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            <h1 className="font-heading font-black text-3xl md:text-4xl">{t("wallet.title")}</h1>

            <div className="wallet-card">
                <div className="relative z-10 space-y-4">
                    <div className="flex items-center gap-2 text-white/70 text-xs font-semibold uppercase tracking-wider">
                        <WalletIcon className="h-4 w-4" />
                        {t("wallet.current_balance")}
                    </div>
                    <div data-testid="wallet-balance" className="font-heading font-black text-5xl md:text-6xl tracking-tight">
                        {formatCurrency(wallet.balance, currency)}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-2">
                        <button
                            data-testid="withdraw-btn"
                            disabled={!payoutMethod || Number(wallet.balance) <= 0}
                            onClick={initiateWithdraw}
                            className="rounded-full bg-white text-foreground px-6 py-3 font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download className="h-4 w-4" />
                            {t("wallet.withdraw")}
                        </button>
                        <button
                            data-testid="setup-payout-btn"
                            onClick={() => setShowSetup(!showSetup)}
                            className="rounded-full bg-white/10 border border-white/20 text-white px-6 py-3 font-bold text-sm inline-flex items-center gap-2 hover:bg-white/20 transition-colors"
                        >
                            <Settings className="h-4 w-4" />
                            {payoutMethod ? t("wallet.edit") : t("wallet.setup_payout")}
                        </button>
                    </div>
                    {!payoutMethod && (
                        <div className="text-xs text-white/70 bg-white/10 p-3 rounded-xl mt-2">{t("wallet.no_payout_method")}</div>
                    )}
                </div>
            </div>

            {showSetup && (
                <div className="bento-card p-5 md:p-6 space-y-4" data-testid="payout-setup">
                    <h2 className="font-bold text-lg">{countryCode === "US" ? t("wallet.bank_account") : t("wallet.moncash_account")}</h2>
                    {countryCode === "US" ? (
                        <>
                            <F label={t("wallet.payee_name")} v={setupData.payee_name} set={(v) => setSetupData({ ...setupData, payee_name: v })} tid="payout-payee-name" />
                            <F label={t("wallet.account_number")} v={setupData.account_number} set={(v) => setSetupData({ ...setupData, account_number: v })} tid="payout-account-number" />
                            <F label={t("wallet.routing_number")} v={setupData.routing_number} set={(v) => setSetupData({ ...setupData, routing_number: v })} tid="payout-routing-number" />
                        </>
                    ) : (
                        <>
                            <F label={t("wallet.full_name")} v={setupData.full_name} set={(v) => setSetupData({ ...setupData, full_name: v })} tid="payout-full-name" />
                            <PhoneInput label={t("wallet.phone_number")} phoneCode={setupData.phone_code || "+509"} phone={setupData.phone} onChange={({ phone_code, phone }) => setSetupData({ ...setupData, phone_code, phone })} testPrefix="payout-phone" />
                        </>
                    )}
                    <div className="p-3 rounded-xl bg-muted text-xs">
                        <span className="font-semibold">{t("wallet.confirm_info")}:</span>{" "}
                        {countryCode === "US"
                            ? `${setupData.payee_name} - ${setupData.account_number} / ${setupData.routing_number}`
                            : `${setupData.full_name} - ${setupData.phone_code}${setupData.phone}`}
                    </div>
                    <button data-testid="save-payout-btn" onClick={initiatePayoutMethod} className="btn-primary">
                        {t("wallet.save_method")}
                    </button>
                </div>
            )}

            <div>
                <h2 className="font-heading font-black text-2xl mb-4 flex items-center gap-2">
                    <History className="h-6 w-6" />
                    {t("wallet.history")}
                </h2>
                {history.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-border p-8 text-center text-muted-foreground">{t("wallet.no_history")}</div>
                ) : (
                    <div className="space-y-2">
                        {history.map((p) => (
                            <div key={p.id} data-testid={`payout-${p.id}`} className="bento-card p-4 flex items-center justify-between">
                                <div>
                                    <div className="font-bold">{formatCurrency(p.amount, p.currency_code)}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {new Date(p.created_at).toLocaleString()}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {p.payout_method_details?.type === "bank_transfer"
                                            ? `Bank - ${p.payout_method_details?.payee_name || ""}`
                                            : `MonCash - ${p.payout_method_details?.full_name || ""}`}
                                    </div>
                                </div>
                                <span className={`badge-pill ${p.status === "processed" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]"}`}>
                                    {p.status === "processed" ? t("wallet.processed") : t("wallet.pending")}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <VerifyCodeDialog
                open={methodVerifyOpen}
                onOpenChange={setMethodVerifyOpen}
                email={profile?.email}
                onSubmit={confirmPayoutMethod}
                onSuccess={() => { setShowSetup(false); load(); }}
                title={t("wallet.confirm_method_title")}
                description={t("auth.enter_code_subtitle", { email: profile?.email || "" })}
                testId="payout-method-verify"
            />
            <VerifyCodeDialog
                open={withdrawVerifyOpen}
                onOpenChange={setWithdrawVerifyOpen}
                email={profile?.email}
                onSubmit={confirmWithdraw}
                onSuccess={() => load()}
                title={t("wallet.confirm_withdraw_title")}
                description={t("auth.enter_code_subtitle", { email: profile?.email || "" })}
                testId="payout-request-verify"
            />
        </div>
    );
}

const F = ({ label, v, set, tid }) => (
    <div>
        <label className="text-sm font-semibold block mb-1.5">{label}</label>
        <input data-testid={tid} value={v || ""} onChange={(e) => set(e.target.value)} className="input-modern" />
    </div>
);
